import dynamic from 'next/dynamic';
import Script from 'next/script';
import { useEffect } from 'react';
import BreadcrumbMillesima from '@/components/common/BreadcrumbMillesima';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { NS } from '@/constants';
import { PAGE_TYPE } from '@/constants/datalayer';
import { FOOTER_TYPE } from '@/constants/footer';
import { URLS } from '@/constants/urls';
import { HrefLangModel } from '@/models';
import { useAppDispatch, useAppSelector } from '@/redux';
import { addCartProduct } from '@/redux/slices/cart.slice';
import { setReloadingOnClick, showCatalogModal } from '@/redux/slices/gui.slice';
import { useServices } from '@/services/useServices';
import { sanitizeUrl } from '@/utils/millesimaUtils';
import styles from './LandingView.module.scss';

const ModalCatalog = dynamic(() => import('@/components/common/ModalCatalog'), { ssr: false });
ModalCatalog.displayName = 'ModalCatalog';

type LandingViewProps = {
	title: string;
	htmlContent: string;
	metaDesc: string;
	noIndex: boolean;
	hrefLang: HrefLangModel[];
	url: string;
	canonical: string;
	baseUrl: string;
	breadcrumbSeo: [];
	landingPageBackgroundColor: string;
	breadcrumbTextColor: string;
	breadcrumbTextHoverColor: string;
	withWrapper: boolean;
};

export const LandingView = ({
	title,
	htmlContent,
	metaDesc,
	noIndex,
	hrefLang,
	url,
	canonical,
	baseUrl,
	breadcrumbSeo,
	landingPageBackgroundColor,
	breadcrumbTextColor,
	breadcrumbTextHoverColor,
	withWrapper,
}: LandingViewProps) => {
	const catalogModalVisible = useAppSelector(({ gui }) => gui.catalogModalVisible);
	const isMobileDevice = useAppSelector(({ gui }) => gui.isMobileDevice);
	const isDesktop = useScreenSizeContext().isDesktop;
	const storeConf = useAppSelector(({ root }) => root.storeConf);
	const dispatch = useAppDispatch();
	const pageUrl = sanitizeUrl(storeConf.url, canonical || url);
	const { datalayerService } = useServices();

	useEffect(() => {
		datalayerService.pushPageName(PAGE_TYPE.LANDING, PAGE_TYPE.LANDING);
	}, []);

	const isDesktopView = !isMobileDevice && isDesktop;

	useEffect(() => {
		(window as any).addProductNext = customAddCartProduct;
		(window as any).openCatalogModal = customShowCatalogModal;
		dispatch(setReloadingOnClick({ isReloading: true }));
	}, [htmlContent]);

	const customShowCatalogModal = () => {
		dispatch(showCatalogModal());
	};

	const customAddCartProduct = (partnumber, qty, options, custoText) => {
		dispatch(
			addCartProduct({
				item: partnumber,
				qty,
				options,
				custoText,
				error: null,
			}),
		);
	};

	return (
		<div style={landingPageBackgroundColor && { background: landingPageBackgroundColor }}>
			<MetaTagsMillesima
				title={title}
				metaDesc={metaDesc}
				originalUrl={pageUrl}
				hrefLang={hrefLang}
				noIndex={noIndex}
				landing
			/>
			<Script defer={true} src={URLS.JQUERY} />
			<Script defer={true} src={URLS.UTILS_MILLESIMA} />

			{isDesktopView && breadcrumbSeo && (
				<div className={`container ${styles['container-width']}`} data-test-id="breadcrumb">
					<BreadcrumbMillesima
						breadcrumbSeo={breadcrumbSeo}
						url={baseUrl}
						withMargin={false}
						textColor={breadcrumbTextColor}
						textHoverColor={breadcrumbTextHoverColor}
					/>
				</div>
			)}

			<div
				className={withWrapper ? styles.wrapper : null}
				dangerouslySetInnerHTML={{ __html: htmlContent }}
				data-test-id="content-html"
			/>

			{catalogModalVisible && <ModalCatalog storeId={storeConf.storeId} locale={storeConf.locale} />}

			{!isDesktopView && breadcrumbSeo && (
				<div className="col" data-test-id="breadcrumb-mobile">
					<BreadcrumbMillesima
						breadcrumbSeo={breadcrumbSeo}
						url={baseUrl}
						withMargin={false}
						textColor={breadcrumbTextColor}
						textHoverColor={breadcrumbTextHoverColor}
					/>
				</div>
			)}
		</div>
	);
};

export default LandingView;

/**
 * Prefetch function  called on server side
 * @param {object} _ctx The server side context
 * @param {object} store The server side application store
 * @param {object} props The seo page props
 * @return {object} Return an object containing the component props
 *    layoutOption : object containing the needed page layout (see _app component)
 *    namespaces : an array containing the needed translation namespaces
 */
export const prefetchView = async (_ctx, { getState }, props) => {
	const { title, htmlContent, metaDesc, noIndex, hrefLang, canonical, url } = props;
	const {
		root: { baseUrl },
	} = getState();
	const namespacesRequired = [NS.COMMON, NS.CATEG, NS.CHECKOUT, NS.ALTER];

	return {
		baseUrl,
		title,
		htmlContent,
		metaDesc,
		noIndex,
		hrefLang,
		url,
		canonical,
		namespacesRequired,
		layoutOptions: {
			footerType: FOOTER_TYPE.LANDING,
		},
	};
};
